import React, { useState, useMemo } from 'react';
import toast from 'react-hot-toast';
import { updateSubPortfolioAllocationV1, updateV2PortfolioAllocation } from '../../composables/fetchAPI';
import { useAllocationValidator, AllocationItem } from '../../hooks/useAllocationValidator';
import { usePortfolio } from '../../context/PortfolioContext';
import { useMarketPrices } from '../../context/MarketPriceContext'; // Import มาที่ Modal นี้
interface EditAllocationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  portfolio: any; // รับก้อน Portfolio ใหญ่เข้ามา
  isSideBySide?: boolean; // ✅ เช็คว่าต้องเปิดข้างๆ ไหม
}

const EditAllocationModal: React.FC<EditAllocationModalProps> = ({ isOpen, onClose, onSave, portfolio, isSideBySide = false }) => {
  const [isLoading, setIsLoading] = useState(false);
  const { displayMode } = usePortfolio(); // ✅ ดึง displayMode จาก context
  const { currentPrices } = useMarketPrices(); // เรียกใช้ Hook ตรงนี้

  // 1. แปลงข้อมูลจาก Portfolio ให้กลายเป็น Format ที่ Hook ต้องการ
  // ✅ 2. เอา useMemo มาครอบ initialItems เอาไว้!
  const initialItems: AllocationItem[] = useMemo(() => {
    // 1. ดึง CashRatio ให้ครอบคลุมทุกโครงสร้าง
    // V2: portfolio.cash?.expectedRatio
    // V1/Legacy: portfolio.cashExpectedRatio
    const cashRatio = portfolio.cash?.expectedRatio ?? portfolio.cashExpectedRatio ?? 0;

    // 2. ดึง SubPorts/Portfolios
    // ถ้ามี subPorts ให้ใช้ subPorts (V2)
    // ถ้าไม่มี ให้มองว่าตัวพอร์ตเองคือพอร์ตย่อย (V1)
    const subPorts = portfolio.subPorts && Array.isArray(portfolio.subPorts)
      ? portfolio.subPorts
      : (portfolio.subPortId || portfolio.id ? [portfolio] : []);

    return [
      { id: 'CASH', name: 'เงินสด (Cash)', ratio: cashRatio, isCash: true },
      ...subPorts
        .filter((sub: any) => sub.status !== 'ARCHIVED' && sub.status !== 'DELETED')
        .map((sub: any) => ({
          // ✅ ใช้ id แบบ Dynamic: ถ้ามี subPortId (V2) ให้ใช้, ถ้าไม่มีให้ใช้ id (V1)
          id: sub.subPortId || sub.id,
          name: sub.name,
          ratio: sub.expectedRatio || 0,
          isCash: false,
        })),
    ];
  }, [portfolio]);
  // 2. เรียกใช้งาน Custom Hook
  const { items, totalRatio, remainingRatio, isValid, isOverAllocated, updateRatio } = useAllocationValidator(initialItems);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid) return toast.error('สัดส่วนรวมต้องไม่เกิน 100%');

    setIsLoading(true);
    try {
      const portId = portfolio.portfolioId || portfolio.portId || portfolio.id;

      if (displayMode === 'V1') {
        // ✅ ใช้ Endpoint ใหม่ตาม Spec ใน PDF
        // สำหรับการแก้สัดส่วนหุ้นในพอร์ตย่อย
        const holdingsPayload = items
          .filter(i => !i.isCash) // เลือกเฉพาะหุ้น
          .map(i => ({
            assetId: i.id, // หรือใช้ stockSymbol แล้วแต่ว่าระบบเดิมเก็บอะไร
            expectedRatio: i.ratio
          }));

        await updateSubPortfolioAllocationV1(portId, {
          cashExpectedRatio: items.find(i => i.isCash)?.ratio || 0,
          subPorts: holdingsPayload.map(i => ({ subPortId: i.assetId, expectedRatio: i.expectedRatio })),
        });
      } else {
        // 🔵 V2: Logic เดิมของคุณ
        const cashItem = items.find((i) => i.isCash);
        const subPortItems = items.filter((i) => !i.isCash).map((i) => ({
          subPortId: i.id,
          expectedRatio: i.ratio,
        }));

        await updateV2PortfolioAllocation(portId, {
          cashExpectedRatio: cashItem?.ratio || 0,
          subPorts: subPortItems,
        });
      }

      toast.success('อัปเดตสัดส่วนสำเร็จ');
      onSave();
      onClose();
    } catch (error: any) {
      toast.error(error.message || 'เกิดข้อผิดพลาด');
    } finally {
      setIsLoading(false);
    }
  };
const totalMarketValue = useMemo(() => {
  if (displayMode !== 'V1') return 0;

  const subPorts = portfolio.subPorts || [];
  
  // 1. คำนวณมูลค่าหุ้นจากทุก subPort
  const stockValue = subPorts.reduce((acc: number, sub: any) => {
    const stocks = sub.stocks || [];
    return acc + stocks.reduce((sum: number, item: any) => {
      const symbol = item.stockSymbol || item.assetSymbol || item.symbol;
      const price = currentPrices[symbol] || 0;
      return sum + ((item.quantity || 0) * price);
    }, 0);
  }, 0);

  // 2. ดึงเงินสดจาก cashBalances (จากข้อมูล JSON ของคุณ)
  const cashAmount = portfolio.cashBalances?.[0]?.amount || 0;

  // 3. รวมเป็นมูลค่าพอร์ตปัจจุบัน (Market Value + Cash) ตามสูตร
  const totalValue = stockValue + cashAmount;

  console.log('Total Portfolio Value (Stocks + Cash):', totalValue);
  return totalValue;
}, [portfolio, displayMode, currentPrices]);

const cashValue = useMemo(() => {
  // ลองหาดูว่าเงินสดในโครงสร้าง V1 ของคุณอยู่ที่ไหน
  return portfolio.cashBalances?.[0]?.amount || 0;
}, [portfolio]);

  if (!isOpen) return null;

  return (
    // ✅ ถ้าเปิดคู่กัน ให้เอา pointer-events-none บล็อกฉากหลังตัวเอง เพื่อให้คลิกหน้าต่างซ้ายได้
    <div className={`fixed inset-0 flex items-center justify-center p-4 ${isSideBySide ? 'z-[101] pointer-events-none' : 'z-[100]'}`}>

      {/* ✅ ถ้าเปิดคู่ ฉากหลังตัวเองจะโปร่งใสไปเลย */}
      <div className={`absolute inset-0 ${isSideBySide ? 'bg-transparent' : 'bg-black/60 backdrop-blur-sm pointer-events-auto'}`} onClick={isSideBySide ? undefined : onClose}></div>

      {/* ✅ ถ้าเปิดคู่ จะขยับไปทางขวา และมีความสูงเท่าหน้าต่างซ้าย */}
      <div className={`relative w-full max-w-sm bg-[#18181b] border border-gray-700 rounded-2xl shadow-2xl flex flex-col pointer-events-auto transition-transform duration-300 max-h-[85vh] ${isSideBySide ? 'md:translate-x-[52%] h-[480px]' : ''}`}>
        <div className="flex-shrink-0 px-6 py-4">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-bold text-white">
              ปรับสัดส่วนเป้าหมาย: <span className={isOverAllocated ? 'text-red-500' : 'text-white'}>{Number(totalRatio.toFixed(2))} %</span>
            </h2>
            <button onClick={onClose} className="text-gray-400 hover:text-white font-bold">&times;</button>
          </div>
          {/* ✅ แถบสถานะแบบภาพ 2 */}
          {/* ✅ แก้ไขแถบสถานะให้วิ่งตาม % และมีพื้นหลังสีเทารองรับ */}
          <div className="w-full bg-gray-800 h-1.5 mt-3 rounded-full overflow-hidden">
            <div
              className={`h-full transition-all duration-500 rounded-full ${totalRatio === 100 ? 'bg-[#22c55e]' : isOverAllocated ? 'bg-red-500' : 'bg-yellow-500'}`}
              style={{ width: `${Math.min(totalRatio, 100)}%` }}
            ></div>
          </div>        </div>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          <div className="flex-1 overflow-y-auto p-6 pt-2 space-y-4 custom-scrollbar">
            {items.map((item) => (
              <div key={item.id} className="flex flex-col bg-[#232428] p-2.5 rounded-lg border border-gray-700/50 mb-2">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-sm font-medium text-gray-300 ml-2">{item.name}</span>
                  <div className="flex items-center gap-2 mr-1">
                    <input
                      type="number" min="0" max="100" step="any"
                      value={item.ratio.toString()}
                      onChange={(e) => {
                        // ให้พิมพ์ทศนิยมหลายตำแหน่งได้ปกติ
                        updateRatio(item.id, e.target.value === '' ? 0 : parseFloat(e.target.value));
                      }}
                      onBlur={(e) => {
                        // ✅ เมื่อพิมพ์เสร็จ (Blur) ค่อยปัดเศษให้เหลือ 2 ตำแหน่ง
                        const val = parseFloat(e.target.value);
                        updateRatio(item.id, isNaN(val) ? 0 : Math.round(val * 100) / 100);
                      }}

                      className="w-20 bg-[#18181b] border border-gray-600 rounded px-2 py-1 text-center text-white focus:outline-none focus:border-[#22c55e]"
                    />
                    <span className="text-gray-400 text-sm">%</span>
                  </div>
                </div>
                {/* ✅ เพิ่มส่วนแสดงมูลค่าเงินสดเป้าหมาย ถ้าเป็น Cash */}
                {item.isCash && (
                  <div className="text-xs text-green-400 ml-2 mt-1">
                    มูลค่าเป้าหมาย: ${((totalMarketValue * item.ratio) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="flex-shrink-0 p-4 border-t border-gray-700 flex justify-between items-center px-6">
            <button type="button" onClick={onClose} className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-500 font-bold text-sm transition-colors">
              ยกเลิก
            </button>
            <button type="submit" disabled={isLoading || isOverAllocated} className="px-6 py-2 bg-[#22c55e] hover:bg-green-400 text-white rounded font-bold text-sm disabled:opacity-50 transition-all">
              {isLoading ? '...' : 'บันทึก'}
            </button>
          </div>
        </form>

      </div>
    </div>
  );
};

export default EditAllocationModal;